import { useState, useMemo, useRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { FiFilter, FiX } from 'react-icons/fi';
import { TPaginationProps, TTableProps } from '../../types/types';
import SearchComponent from '../CoreComponents/Search';
import Pagination from '../Pagination/Pagination';

type TProps<T = any> = {
  tableProps: TTableProps<T>;
  pagination?: TPaginationProps<T>;
  rowsPerPage?: number;
  withSearch?: boolean;
  handleSearch?: (term: string) => void;
  noMargin?: boolean;
  onRowClick?: (row: any) => void;
  onFilterChange?: (filters: Record<string, string[]>) => void;
  /** Initial filter state. Only applied on component mount, not on subsequent prop updates. */
  initialFilters?: Record<string, string[]>;
};

export default function FilterableTable<T = any>({
  tableProps,
  pagination,
  rowsPerPage = 25,
  withSearch = false,
  handleSearch,
  noMargin = false,
  onRowClick,
  onFilterChange,
  initialFilters = {},
}: TProps<T>) {
  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = tableProps;
  // Note: initialFilters is only used for initial state and does not sync with prop updates
  const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>(initialFilters);
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const filterDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (filterDropdownRef.current && !filterDropdownRef.current.contains(event.target as Node)) {
        setOpenFilterColumn(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset to page 1 when rows change (e.g., from search)
  useEffect(() => {
    setCurrentPage(1);
  }, [rows.length]);

  // Notify parent of filter changes (done in useEffect to avoid render phase updates)
  useEffect(() => {
    if (onFilterChange) {
      onFilterChange(columnFilters);
    }
  }, [columnFilters, onFilterChange]);

  // Helper function to filter rows by all filters except the specified column
  const getRowsFilteredByOtherColumns = (excludeColumnId: string) => {
    return rows.filter(row => {
      return Object.entries(columnFilters).every(([filterId, filterValues]) => {
        // Skip the excluded column's filter
        if (filterId === excludeColumnId || filterValues.length === 0) return true;
        
        const cellValue = row.values[filterId];
        const filterColumn = headerGroups[0]?.headers.find(h => h.id === filterId);
        const isDateColumn = (filterColumn as any)?.filterType === 'date';
        const getFilterValueForColumn = (filterColumn as any)?.getFilterValue;
        
        if (isDateColumn && cellValue) {
          const dateStr = String(cellValue).split('T')[0];
          return filterValues.includes(dateStr);
        } else {
          const compareValue = getFilterValueForColumn ? getFilterValueForColumn(cellValue) : String(cellValue || '');
          return filterValues.includes(compareValue);
        }
      });
    });
  };

  // Get unique values for a column (based on currently filtered data, excluding this column's filter)
  const getUniqueValues = (columnId: string): string[] => {
    const values = new Set<string>();
    const column = headerGroups[0]?.headers.find(h => h.id === columnId);
    const getFilterValue = (column as any)?.getFilterValue;
    
    const rowsToCheck = getRowsFilteredByOtherColumns(columnId);
    
    rowsToCheck.forEach(row => {
      const value = row.values[columnId];
      if (value !== null && value !== undefined && value !== '') {
        // Use getFilterValue if available, otherwise use the raw value
        const displayValue = getFilterValue ? getFilterValue(value) : String(value);
        values.add(displayValue);
      }
    });
    return Array.from(values).sort();
  };

  // Get unique dates for date columns (just the date part, not time)
  const getUniqueDates = (columnId: string): string[] => {
    const dates = new Set<string>();
    
    const rowsToCheck = getRowsFilteredByOtherColumns(columnId);
    
    rowsToCheck.forEach(row => {
      const value = row.values[columnId];
      if (value) {
        // Extract just the date part
        const dateStr = String(value).split('T')[0];
        dates.add(dateStr);
      }
    });
    return Array.from(dates).sort().reverse(); // Most recent first
  };

  // Toggle filter for a column value
  const toggleFilter = (columnId: string, value: string) => {
    setColumnFilters(prev => {
      const currentFilters = prev[columnId] || [];
      const newFilters = currentFilters.includes(value)
        ? currentFilters.filter(v => v !== value)
        : [...currentFilters, value];

      const updated = {
        ...prev,
        [columnId]: newFilters,
      };

      // Remove empty filter arrays
      if (newFilters.length === 0) {
        delete updated[columnId];
      }

      return updated;
    });
  };

  // Clear all filters for a column
  const clearColumnFilter = (columnId: string) => {
    setColumnFilters(prev => {
      const updated = { ...prev };
      delete updated[columnId];
      return updated;
    });
  };

  // Clear all filters
  const clearAllFilters = () => {
    setColumnFilters({});
  };

  // Filter rows based on active filters
  const filteredRows = useMemo(() => {
    if (Object.keys(columnFilters).length === 0) {
      return rows;
    }

    return rows.filter(row => {
      return Object.entries(columnFilters).every(([columnId, filterValues]) => {
        if (filterValues.length === 0) return true;
        
        const cellValue = row.values[columnId];
        // Check if this column is a date column or has custom filter value
        const column = headerGroups[0]?.headers.find(h => h.id === columnId);
        const isDateColumn = (column as any)?.filterType === 'date';
        const getFilterValue = (column as any)?.getFilterValue;
        
        if (isDateColumn && cellValue) {
          // For date columns, compare just the date part
          const dateStr = String(cellValue).split('T')[0];
          return filterValues.includes(dateStr);
        } else {
          // Use getFilterValue if available for comparison
          const compareValue = getFilterValue ? getFilterValue(cellValue) : String(cellValue || '');
          return filterValues.includes(compareValue);
        }
      });
    });
  }, [rows, columnFilters, headerGroups]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [columnFilters]);

  // Calculate pagination
  const totalPages = Math.ceil(filteredRows.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedRows = filteredRows.slice(startIndex, endIndex);

  const hasActiveFilters = Object.keys(columnFilters).length > 0;

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  return (
    <div className={noMargin ? '' : 'mt-4'}>
      {/* Search and Filter Controls */}
      <div className="flex justify-between items-center mb-4">
        {withSearch && (
          <div className="flex-grow mr-4">
            <SearchComponent onSearch={handleSearch} />
          </div>
        )}
        {hasActiveFilters && (
          <button
            onClick={clearAllFilters}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
          >
            <FiX /> Clear All Filters
          </button>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table {...getTableProps()} className="min-w-full text-left text-sm">
          <thead className="border-b font-medium dark:border-neutral-500 uppercase">
            {headerGroups.map(headerGroup => {
              const { key, ...restHeaderGroupProps } = headerGroup.getHeaderGroupProps();
              return (
                <tr key={key} {...restHeaderGroupProps}>
                  {headerGroup.headers.map(column => {
                    const { key, ...restColumnProps } = column.getHeaderProps();
                    const columnId = column.id;
                    const hasFilter = columnFilters[columnId]?.length > 0;
                    const disableFilter = (column as any).disableFilter === true;
                    const filterType = (column as any).filterType;
                    const isDateColumn = filterType === 'date';
                    const uniqueValues = isDateColumn ? getUniqueDates(columnId) : getUniqueValues(columnId);
                    const columnWidth = (column as any).width;

                    return (
                      <th 
                        className="px-3 py-2 text-left relative whitespace-nowrap" 
                        key={key} 
                        {...restColumnProps}
                        style={columnWidth ? { width: `${columnWidth}px`, minWidth: `${columnWidth}px` } : undefined}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span>{column.render('Header')}</span>
                          {!disableFilter && (
                            <div className="relative">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setOpenFilterColumn(openFilterColumn === columnId ? null : columnId);
                                }}
                                className={twMerge(
                                  'p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors',
                                  hasFilter && 'text-orange-1000'
                                )}
                                title="Filter column"
                              >
                                <FiFilter size={16} />
                              </button>

                            {/* Filter Dropdown */}
                            {openFilterColumn === columnId && (
                              <div
                                ref={filterDropdownRef}
                                className="absolute top-full right-0 mt-1 w-64 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded shadow-lg z-50 max-h-80 overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <div className="p-2 border-b border-gray-200 dark:border-gray-700 flex justify-between items-center sticky top-0 bg-white dark:bg-gray-800">
                                  <span className="font-semibold text-xs">Filter Options</span>
                                  {hasFilter && (
                                    <button
                                      onClick={() => clearColumnFilter(columnId)}
                                      className="text-xs text-red-500 hover:text-red-700"
                                    >
                                      Clear
                                    </button>
                                  )}
                                </div>
                                <div className="p-2">
                                  {uniqueValues.length === 0 ? (
                                    <div className="text-xs text-gray-500 p-2">No values to filter</div>
                                  ) : (
                                    uniqueValues.map(value => {
                                      const isChecked = columnFilters[columnId]?.includes(value) ?? false;
                                      return (
                                        <label
                                          key={value}
                                          className="flex items-center gap-2 p-2 hover:bg-orange-1000 hover:text-white rounded cursor-pointer text-xs"
                                        >
                                          <input
                                            type="checkbox"
                                            checked={isChecked}
                                            onChange={() => toggleFilter(columnId, value)}
                                            className="cursor-pointer"
                                          />
                                          <span className="truncate">{value || '(empty)'}</span>
                                        </label>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            )}
                            </div>
                          )}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              );
            })}
          </thead>
          <tbody {...getTableBodyProps()}>
            {(pagination?.currentRows || paginatedRows).map((row, i) => {
              prepareRow(row);
              const { key, ...restRowProps } = row.getRowProps();
              return (
                <tr
                  key={key}
                  onClick={() => onRowClick && onRowClick(row.original)}
                  className={twMerge(
                    'text-base',
                    onRowClick && 'cursor-pointer hover:bg-orange-1000 hover:text-white',
                    i % 2 === 0
                      ? 'border-b bg-white dark:border-neutral-500 dark:bg-neutral-600'
                      : 'border-b bg-neutral-100 dark:border-neutral-500 dark:bg-neutral-700'
                  )}
                  {...restRowProps}
                >
                  {row.cells.map(cell => {
                    const { key, ...restCellProps } = cell.getCellProps();
                    return (
                      <td className="px-3 py-2 text-left" key={key} {...restCellProps}>
                        {cell.render('Cell')}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Active Filters Display */}
      {hasActiveFilters && (
        <div className="mt-2 flex flex-wrap gap-2">
          {Object.entries(columnFilters).map(([columnId, values]) => {
            if (values.length === 0) return null;
            const column = headerGroups[0].headers.find(h => h.id === columnId);
            const columnName = column?.Header || columnId;
            
            return values.map(value => (
              <span
                key={`${columnId}-${value}`}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 rounded"
              >
                <strong>{columnName}:</strong> {value}
                <button
                  onClick={() => toggleFilter(columnId, value)}
                  className="hover:text-orange-1000 ml-1"
                >
                  <FiX size={12} />
                </button>
              </span>
            ));
          })}
        </div>
      )}

      {/* Pagination */}
      {!pagination && totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          handleNextPage={handleNextPage}
          handlePreviousPage={handlePreviousPage}
        />
      )}
      {pagination && (
        <Pagination
          currentPage={pagination.currentPage}
          totalPages={pagination.totalPages}
          handleNextPage={pagination.handleNextPage}
          handlePreviousPage={pagination.handlePreviousPage}
        />
      )}
    </div>
  );
}
